/**
 * Tiny insertion-ordered LRU map.
 *
 * Backed by a JS `Map`, which preserves insertion order — so the first key is
 * always the least-recently-used. `get`/`set` on an existing key re-inserts it
 * (moving it to the most-recently-used end); `set` past `capacity` evicts the
 * oldest. Used for process-lifetime caches (e.g. link-preview unfurls) that
 * would otherwise grow unbounded across a long session in a busy channel.
 */
export class LruMap<K, V> {
  private readonly store = new Map<K, V>();

  constructor(private readonly capacity: number) {
    if (capacity < 1) throw new Error('LruMap capacity must be >= 1');
  }

  has(key: K): boolean {
    return this.store.has(key);
  }

  get(key: K): V | undefined {
    if (!this.store.has(key)) return undefined;
    // Touch: delete + re-set moves the entry to the most-recently-used end.
    // `has` guard above guarantees the key exists, so the value is present.
    const value = this.store.get(key)!;
    this.store.delete(key);
    this.store.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    // Re-set on an existing key must refresh its recency, so drop it first.
    if (this.store.has(key)) this.store.delete(key);
    this.store.set(key, value);
    if (this.store.size > this.capacity) {
      // Evict the least-recently-used (the first/oldest key). size > capacity
      // (>= 2) guarantees at least one key exists.
      const oldest = this.store.keys().next().value!;
      this.store.delete(oldest);
    }
  }

  get size(): number {
    return this.store.size;
  }
}
